import {
  ChangeDetectionStrategy,
  Component,
  computed,
  effect,
  inject,
  input,
  signal,
} from '@angular/core'
import { fadeScaleAnimation } from 'projects/commons/src/lib/animations'
import {
  MixingEvent,
  SpeedPatternStage,
} from 'projects/commons/src/lib/types/mixing'
import { MixingDotsComponent } from '../mixing-dots/mixing-dots.component'
import { MixingStatusComponent } from '../mixing-status/mixing-status.component'
import { CommonModule, DecimalPipe, UpperCasePipe } from '@angular/common'
import { RpmPipe } from 'projects/tools/src/lib/rpm.pipe'
import { ProgressBarComponent } from '../progress-bar/progress-bar.component'
import { NetworkService } from 'projects/commons/src/lib/services/network.service'
import { merge, Observable } from 'rxjs'
import { debounceTime, filter, map, startWith, tap } from 'rxjs/operators'
import { toObservable, toSignal } from '@angular/core/rxjs-interop'

export type SpeedStatus = 'slow' | 'in_range' | 'fast' | ''
@Component({
  standalone: true,
  selector: 'cyc-mixing',
  templateUrl: './mixing.component.html',
  styleUrl: './mixing.component.scss',
  animations: [fadeScaleAnimation],
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [
    CommonModule,
    MixingDotsComponent,
    MixingStatusComponent,
    RpmPipe,
    ProgressBarComponent,
    UpperCasePipe,
  ],
  providers: [NetworkService],
  host: {
    '[class.cyc-mixing]': 'true',
    '[class.cyc-mixing--responsive]': 'responsive()',
    '[class.cyc-mixing--in-progress]': 'inProgress()',
    '[class.cyc-mixing--have-event]': 'haveEvent()',
  },
})
export class MixingComponent {
  private networkSvc = inject(NetworkService)

  mode = input<'truck' | 'batcher'>('truck')
  mixingType = input<'batch-mixing' | 'site-mixing'>('batch-mixing')
  responsive = input(true)
  showHeader = input(false)
  allowDebug = input(false)
  registration = input('')
  plant = input('Peschiera Borromeo')
  loadingPoint = input('Loading point 2')
  mixingEvent = input<MixingEvent | null>(null)
  drumSpeed = input<number>(null)
  successTimeout = input(5000)

  public onLine = this.networkSvc.onLine
  public debug = signal(false)
  private prevStage: SpeedPatternStage | null = null

  // haveEvent for the host class binding above
  public haveEvent = computed(() => {
    const mixingEvent = this.mixingEvent()
    return !!mixingEvent
  })
  public inProgress = computed(
    () => this.mixingEvent()?.status === 'in_progress',
  )

  // START OF SUCCESS
  // in 'batcher' mode, we show the success message for 10s on success
  private prevEvent: MixingEvent | null = null
  private _successTrigger = signal(false)
  public successTrigger = this._successTrigger.asReadonly()

  private showSuccessTrigger$ = toObservable(this.successTrigger).pipe(
    filter((val) => val === true),
  )
  // convert the triggger=true into 2 events: true then false
  private showSuccess$: Observable<boolean> = merge(
    this.showSuccessTrigger$,
    this.showSuccessTrigger$.pipe(
      debounceTime(this.successTimeout()),
      map(() => false),
    ),
  ).pipe(startWith(false))
  public showSuccess = toSignal(this.showSuccess$)

  public showWaiting = computed(() => {
    const mode = this.mode()
    if (mode === 'truck') {
      // if the context is a truck's mixing, we don't show the waiting screen
      return false
    }
    // batcher, show waiting screen if not in progress or showing Success message
    return !this.inProgress() && !this.showSuccess()
  })

  public currentStage = computed(() => {
    const mixingEvent = this.mixingEvent()
    return mixingEvent?.status === 'in_progress'
      ? mixingEvent.speedPattern.find(
          (stage) =>
            stage.status === 'preparing' || stage.status === 'in_progress',
        )
      : null
  })

  private intervalId = 0
  private _secondsRemaining = signal(0)
  public secondsRemaining = this._secondsRemaining.asReadonly()

  public percentComplete = computed(() => {
    const currentStage = this.currentStage()
    const secondsRemaining = this.secondsRemaining()

    let percentComplete = 0
    if (currentStage) {
      const currentIsActive = secondsRemaining > 0

      if (currentIsActive) {
        const secondsElapsed = currentStage.secondsTarget - secondsRemaining
        percentComplete = Math.round(
          (secondsElapsed / currentStage.secondsTarget) * 100,
        )
      } else {
        percentComplete = 100
      }
    }
    return percentComplete
  })

  constructor() {
    effect(() => {
      const mixingEvent = this.mixingEvent()
      const mode = this.mode()
      let justBecomeSuccessful = false
      if (mode === 'batcher') {
        // if it's just become successful, we don't show the waiting screen for 10 seconds
        // we'll show a success message instead
        const lastUpdateWasSuccessful = this.prevEvent?.status === 'success'
        const thisUpdateIsSuccessful = mixingEvent?.status === 'success'
        justBecomeSuccessful =
          !lastUpdateWasSuccessful && thisUpdateIsSuccessful
      }
      this.prevEvent = Object.assign({}, mixingEvent)
      this._successTrigger.set(justBecomeSuccessful)
    })

    effect((onCleanup) => {
      const currentStage = this.currentStage()
      const stageChanged =
        JSON.stringify(currentStage) !== JSON.stringify(this.prevStage)
      if (stageChanged) {
        this.prevStage = Object.assign({}, currentStage)

        this.stopUpdates()
        if (currentStage?.status === 'in_progress') {
          // setup repeated updates starting in 1 second
          this.intervalId = window.setInterval(
            () => this.updateSecondsRemaining(),
            1000,
          )
        }
        // run the first update immediately
        this.updateSecondsRemaining()
      }
      // ensure the interval is always cleared when this effect is torn down
      onCleanup(() => this.stopUpdates())
    })
  }

  private stopUpdates() {
    // console.log('stopUpdates')
    if (this.intervalId) {
      window.clearInterval(this.intervalId)
      this.intervalId = 0
    }
  }

  private updateSecondsRemaining() {
    // console.group('updateSecondsRemaining')
    let secondsRemaining = 0
    const currentStage = this.currentStage()
    if (currentStage) {
      const lastUpdateTimestamp = this.mixingEvent()?.lastUpdateTimestamp
      // minus the elapsed time from the target time
      secondsRemaining =
        this.prevStage.secondsTarget - this.prevStage.secondsElapsed
      // check if we have a timestamp for the last mixing update
      if (lastUpdateTimestamp && this.prevStage?.status === 'in_progress') {
        // if we have this value, then we're in the live view and getting updates
        // adjust for time since last update
        const lastUpdate = new Date(lastUpdateTimestamp)
        const now = new Date()
        const secondsSinceUpdate = Math.floor(
          (now.getTime() - lastUpdate.getTime()) / 1000,
        )
        // console.log('secondsSinceUpdate', secondsSinceUpdate)
        secondsRemaining -= secondsSinceUpdate
      }
    }
    // if we have a negative value, set to 0
    secondsRemaining = Math.max(0, secondsRemaining)
    this._secondsRemaining.set(secondsRemaining)
    // console.groupEnd()
    const contin =
      secondsRemaining > 0 && this.prevStage?.status === 'in_progress'
    if (!contin) {
      this.stopUpdates()
    }
  }

  public speedStatus = computed(() => {
    const currentStage = this.currentStage()
    const drumSpeed = this.drumSpeed()
    let speedStatus: SpeedStatus = ''
    if (currentStage && drumSpeed) {
      const drumSpeedRounded = Math.round(drumSpeed * 10) / 10
      if (drumSpeedRounded < currentStage?.rpmMin) {
        speedStatus = 'slow'
      } else if (drumSpeedRounded > currentStage?.rpmMax) {
        speedStatus = 'fast'
      } else {
        speedStatus = 'in_range'
      }
    }
    return speedStatus
  })

  public toggleDebug() {
    if (this.allowDebug()) {
      this.debug.set(!this.debug())
    }
  }
}
